import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { platform, type LeadPlan, type LeadStatus } from '../lib/api.js'
import { Badge, Card, Empty, Select, Spinner, toast } from '../components/ui/index.js'
import { useI18n } from '../lib/i18n.js'
import { countryName, countryOptions } from '../lib/countries.js'

const STATUSES: LeadStatus[] = ['new', 'contacted', 'closed', 'spam']
const PLANS: LeadPlan[] = ['small_business', 'pro', 'notify']
const STATUS_COLOR = { new: 'yellow', contacted: 'indigo', closed: 'green', spam: 'gray' } as const

/**
 * Plataforma → Leads (#293): pedidos "Mais informações" (Small Business /
 * Pro) e "avise-me" vindos da landing e do console (POST /public/leads).
 * Cada lead novo também chega por e-mail (LEADS_NOTIFY_EMAIL). O país
 * informado no pedido aparece em cada lead e serve de filtro (?country=).
 */
export function PlatformLeads() {
  const { t, lang } = useI18n()
  const qc = useQueryClient()
  const [status, setStatus] = useState<LeadStatus | ''>('')
  const [plan, setPlan] = useState<LeadPlan | ''>('')
  const [country, setCountry] = useState('')
  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['platform-leads', status, plan, country],
    queryFn: () =>
      platform.listLeads({
        ...(status ? { status } : {}),
        ...(plan ? { plan } : {}),
        ...(country ? { country } : {}),
      }),
  })
  // países da API da #313 (tabela countries) + os que já aparecem nos leads
  const { data: allCountries } = useQuery({
    queryKey: ['platform-countries'],
    queryFn: platform.listCountries,
    staleTime: 5 * 60_000,
  })
  const countryOpts = useMemo(
    () =>
      countryOptions([allCountries?.map((c) => c.code), leads.map((l) => l.country)], lang, [
        country,
      ]),
    [allCountries, leads, lang, country],
  )
  const setLeadStatus = useMutation({
    mutationFn: (v: { id: string; status: LeadStatus }) => platform.setLeadStatus(v.id, v.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-leads'] }),
    onError: () => toast.error(t('admin.leads.save_error')),
  })

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card title={t('admin.leads.title')} description={t('admin.leads.desc')}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <Select
            label={t('admin.leads.filter_status')}
            value={status}
            data-testid="leads-filter-status"
            onChange={(e) => setStatus(e.target.value as LeadStatus | '')}
            options={[
              { value: '', label: t('admin.leads.all') },
              ...STATUSES.map((s) => ({ value: s, label: t(`admin.leads.status_${s}`) })),
            ]}
          />
          <Select
            label={t('admin.leads.filter_plan')}
            value={plan}
            data-testid="leads-filter-plan"
            onChange={(e) => setPlan(e.target.value as LeadPlan | '')}
            options={[
              { value: '', label: t('admin.leads.all') },
              ...PLANS.map((p) => ({ value: p, label: t(`admin.leads.plan_${p}`) })),
            ]}
          />
          <Select
            label={t('admin.leads.filter_country')}
            value={country}
            data-testid="leads-filter-country"
            onChange={(e) => setCountry(e.target.value)}
            options={[{ value: '', label: t('admin.leads.all') }, ...countryOpts]}
          />
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="w-5 h-5 text-indigo-400" />
          </div>
        ) : leads.length === 0 ? (
          <Empty title={t('admin.leads.empty_title')} description={t('admin.leads.empty_hint')} />
        ) : (
          <ul className="divide-y divide-gray-100" data-testid="leads-list">
            {leads.map((l) => (
              <li key={l.id} className="py-4" data-testid={`lead-${l.id}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge color={STATUS_COLOR[l.status]}>
                        {t(`admin.leads.status_${l.status}`)}
                      </Badge>
                      <Badge color="indigo">{t(`admin.leads.plan_${l.plan}`)}</Badge>
                      <span data-testid={`lead-${l.id}-country`} title={l.country}>
                        <Badge color="gray">
                          {countryName(l.country, lang)} ({l.country})
                        </Badge>
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(l.created_at * 1000).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-800">
                      {l.contact_name}
                      {l.company_name ? ` · ${l.company_name}` : ''} ·{' '}
                      <a className="text-indigo-600" href={`mailto:${l.email}`}>
                        {l.email}
                      </a>
                    </p>
                    {l.details && (
                      <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{l.details}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-400">
                      {l.source_page} · {l.locale}
                      {l.detected_country && l.detected_country !== l.country
                        ? ` · ${t('admin.leads.detected')}: ${countryName(l.detected_country, lang)} (${l.detected_country})`
                        : ''}
                      {l.notified_at ? '' : ` · ${t('admin.leads.not_notified')}`}
                    </p>
                  </div>
                  <select
                    aria-label={t('admin.leads.filter_status')}
                    data-testid={`lead-${l.id}-status`}
                    className="text-sm border border-gray-200 rounded-md px-2 py-1"
                    value={l.status}
                    disabled={setLeadStatus.isPending}
                    onChange={(e) =>
                      setLeadStatus.mutate({ id: l.id, status: e.target.value as LeadStatus })
                    }
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`admin.leads.status_${s}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
